// QLOD-290. Emparejamiento columna del excel <-> sonda del ensayo, sin dependencias de Angular.
//
// Antes, con un registrador multicanal de 12 canales y un ensayo de 4 sondas, se leian solo las 4
// primeras columnas y, como ninguna cabecera casaba con el nombre del canal, se asignaban POR
// POSICION sin avisar: lecturas de un canal guardadas en la sonda de otro. Aqui la regla es fallar en
// cerrado: si no se puede decir sin ambiguedad de que columna sale cada sonda evaluada, error y no se
// sube nada. Que identifica a la columna de un canal (cabecera exacta == descripcion del canal) es la
// regla que ya habia; no se inventa otra.

export interface ExcelSensorColumn {
  /** Posicion de la columna de datos en el fichero (0 = primera columna tras fecha/hora). */
  position: number;
  /** Cabecera de la columna, si el formato la trae. */
  header: string;
}

export interface ExcelSensorTarget {
  equipmentName: string;
  serialNum: string;
  multiChannelName?: string;
  idMultichannel?: number;
  evaluate: boolean;
}

export interface ExcelSensorAssignment {
  /** Indice de la sonda en la lista COMPLETA de sondas de la variable. */
  sensorIndex: number;
  /** Posicion de la columna de la que salen sus lecturas. */
  position: number;
}

export type ExcelSensorMatch =
  { ok: true; mode: 'name' | 'serialNum' | 'position'; assignments: ExcelSensorAssignment[] } |
  { ok: false; error: string };

const norm = (v: string | number | null | undefined): string => (v == null ? '' : `${v}`.trim());

const isMultichannel = (s: ExcelSensorTarget): boolean => s.idMultichannel != null;

/** Nombre con el que una sonda se busca en las cabeceras: el del canal si es multicanal. */
export const sensorExcelName = (s: ExcelSensorTarget): string => norm(isMultichannel(s) ? s.multiChannelName : s.equipmentName);

const describe = (s: ExcelSensorTarget): string => sensorExcelName(s) || norm(s.serialNum) || '(sin nombre)';

/**
 * Subida masiva: decide de que columna salen las lecturas de cada sonda evaluada.
 * Mismo orden de preferencia que antes (nombre, numero de serie, posicion), pero cada modo exige
 * que el emparejamiento sea completo y univoco.
 */
export const matchExcelColumnsToSensors = (columns: ExcelSensorColumn[], sensors: ExcelSensorTarget[]): ExcelSensorMatch => {
  const evaluated = sensors.map((s, sensorIndex) => ({ s, sensorIndex })).filter(x => x.s.evaluate);

  if (columns.length === 0) {
    return { ok: false, error: 'El fichero no tiene columnas de lecturas.' };
  }
  if (evaluated.length === 0) {
    return { ok: false, error: 'No hay sondas evaluadas en la variable.' };
  }

  const headers = columns.map(c => norm(c.header));

  const byKey = (keyOf: (s: ExcelSensorTarget) => string, mode: 'name' | 'serialNum'): ExcelSensorMatch => {
    const assignments: ExcelSensorAssignment[] = [];
    const missing: string[] = [];
    const duplicated: string[] = [];
    const shared = new Map<string, string[]>();

    evaluated.forEach(({ s, sensorIndex }) => {
      const key = keyOf(s);
      const found = key === '' ? [] : columns.filter((_, i) => headers[i] === key);

      if (found.length === 0) {
        missing.push(describe(s));
      } else if (found.length > 1) {
        duplicated.push(key);
      } else {
        assignments.push({ sensorIndex, position: found[0].position });
        shared.set(key, [...(shared.get(key) ?? []), describe(s)]);
      }
    });

    const errors: string[] = [];
    if (missing.length > 0) {
      errors.push(`Ninguna columna del fichero corresponde a: ${missing.join(', ')}.`);
    }
    if (duplicated.length > 0) {
      errors.push(`Hay varias columnas con la misma cabecera: ${[...new Set(duplicated)].join(', ')}.`);
    }
    shared.forEach((names, key) => {
      if (names.length > 1) {
        errors.push(`La columna "${key}" serviria a varias sondas a la vez (${names.join(', ')}).`);
      }
    });

    if (errors.length > 0) {
      return { ok: false, error: `${errors.join(' ')} No se ha subido ningun dato.` };
    }

    return { ok: true, mode, assignments };
  };

  const evaluatedNames = new Set(evaluated.map(x => sensorExcelName(x.s)).filter(n => n !== ''));
  const evaluatedSerials = new Set(evaluated.map(x => norm(x.s.serialNum)).filter(n => n !== ''));

  if (headers.some(h => evaluatedNames.has(h))) {
    return byKey(sensorExcelName, 'name');
  }
  if (headers.some(h => evaluatedSerials.has(h))) {
    return byKey(s => norm(s.serialNum), 'serialNum');
  }

  // Por posicion solo cuando no cabe otra lectura: sin canales de multicanal (su orden en el
  // ensayo no dice nada del orden de los canales en el fichero) y con tantas columnas como sondas.
  // La columna j va a la sonda j de la lista completa, que es lo que se guardaba antes.
  const multichannel = sensors.filter(isMultichannel);
  if (multichannel.length > 0) {
    return {
      ok: false, error: `Las cabeceras del fichero no coinciden con el nombre de ningun canal (${multichannel.map(describe).join(', ')}) ` +
        'y un multicanal no se puede emparejar por posicion. Pon como cabecera de cada columna el nombre del canal. ' +
        'No se ha subido ningun dato.'
    };
  }
  if (columns.length !== sensors.length) {
    return {
      ok: false, error: `Las cabeceras no coinciden con ninguna sonda y el fichero tiene ${columns.length} columnas de lecturas ` +
        `para ${sensors.length} sondas, asi que tampoco se puede emparejar por posicion. No se ha subido ningun dato.`
    };
  }

  const ordered = [...columns].sort((a, b) => a.position - b.position);
  return {
    ok: true, mode: 'position',
    assignments: sensors.map((s, sensorIndex) => ({ sensorIndex, position: ordered[sensorIndex].position }))
  };
};

/**
 * Subida a una sola sonda. Si el fichero trae una sola columna de lecturas, es esa. Si trae varias,
 * solo vale la que se llame exactamente como la sonda (o su canal); antes se cogia siempre la primera.
 * Devuelve la posicion de la columna, o un error.
 */
export const matchExcelColumnToSensor = (columns: ExcelSensorColumn[], sensor: ExcelSensorTarget):
  { ok: true; position: number | null } | { ok: false; error: string } => {
  if (columns.length <= 1) {
    return { ok: true, position: columns.length === 1 ? columns[0].position : null };
  }

  const keys = [sensorExcelName(sensor), norm(sensor.serialNum)].filter(k => k !== '');
  for (const key of keys) {
    const found = columns.filter(c => norm(c.header) === key);
    if (found.length === 1) {
      return { ok: true, position: found[0].position };
    }
    if (found.length > 1) {
      return { ok: false, error: `El fichero tiene varias columnas "${key}". No se ha subido ningun dato.` };
    }
  }

  return {
    ok: false, error: `El fichero tiene ${columns.length} columnas de lecturas y ninguna se llama "${describe(sensor)}". ` +
      'Usa la subida masiva o deja en el fichero solo la columna de esta sonda. No se ha subido ningun dato.'
  };
};

type SensorDataLike = { posEquipment?: number; equipmentName?: string; value?: number };

/**
 * Columnas de lecturas presentes en lo que devuelve ExcelUtils.excelToSensorData (por posEquipment).
 * Una columna sin cabecera y sin ninguna lectura numerica no cuenta: Excel suele guardar columnas
 * vacias al final (el fichero de QLOD-290 trae 48 columnas para 12 canales).
 */
export const excelColumnsFromSensorData = (data: SensorDataLike[]): ExcelSensorColumn[] => {
  const columns = new Map<number, ExcelSensorColumn>();
  const withValues = new Set<number>();

  data.forEach(item => {
    if (item.posEquipment == null) {
      return;
    }
    if (!columns.has(item.posEquipment)) {
      columns.set(item.posEquipment, { position: item.posEquipment, header: norm(item.equipmentName) });
    }
    if (item.value != null && Number.isFinite(item.value)) {
      withValues.add(item.posEquipment);
    }
  });

  return [...columns.values()]
    .filter(c => c.header !== '' || withValues.has(c.position))
    .sort((a, b) => a.position - b.position);
};

/**
 * Subida a una sola sonda: deja solo las lecturas de su columna, o lanza un Error con el motivo
 * (lo recoge el .catch de quien llama, igual que el 'Formato desconocido' de ExcelUtils).
 */
export const keepSensorColumn = <T extends SensorDataLike>(
  data: T[], sensor: ExcelSensorTarget): T[] => {
  const pick = matchExcelColumnToSensor(excelColumnsFromSensorData(data), sensor);

  if (pick.ok === false) {
    throw new Error(pick.error);
  }

  return pick.position == null ? data : data.filter(item => item.posEquipment === pick.position);
};
